// src/frontend/components/layout/NotificationCenter.jsx
import React, {useEffect, useRef} from 'react';
import {
    BsCheckCircle,
    BsExclamationTriangle,
    BsFileEarmarkArrowUp,
    BsInfoCircle,
    BsPersonPlus,
    BsPersonX,
    BsX
} from 'react-icons/bs';
import {useNotifications} from '../../context/NotificationContext';

export default function NotificationCenter({isOpen, onClose}) {
    const {notifications, markAsRead, removeNotification, clearAll} = useNotifications();
    const dropdownRef = useRef(null);

    // Klicke außerhalb → schließen
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    const getIcon = (type = 'info') => {
        switch (type) {
            case 'success':
                return <BsCheckCircle className="text-green-500"/>;
            case 'warning':
                return <BsExclamationTriangle className="text-yellow-500"/>;
            case 'error':
                return <BsX className="text-red-500"/>;
            case 'user-joined':
                return <BsPersonPlus className="text-blue-500"/>;
            case 'user-left':
                return <BsPersonX className="text-purple-500"/>;
            case 'file-uploaded':
                return <BsFileEarmarkArrowUp className="text-indigo-500"/>;
            default:
                return <BsInfoCircle className="text-blue-500"/>;
        }
    };

    return (
        <div
            ref={dropdownRef}
            className={`absolute top-16 right-4 w-96 max-h-[80vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50 transform transition-all duration-300 ease-out ${
                isOpen
                    ? 'scale-100 opacity-100 translate-y-0'
                    : 'scale-95 opacity-0 -translate-y-2 pointer-events-none'
            }`}
        >
            {/* Header */}
            <div
                className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                <h3 className="font-semibold text-lg text-gray-800 dark:text-white">Benachrichtigungen</h3>
                <div className="flex items-center gap-4">
                    {notifications.length > 0 && (
                        <button
                            onClick={clearAll}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                            Alle löschen
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        aria-label="Schließen"
                    >
                        <BsX size={24}/>
                    </button>
                </div>
            </div>

            {/* Liste */}
            <div className="max-h-[calc(80vh-80px)] overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                        Keine Benachrichtigungen
                    </div>
                ) : (
                    notifications.map((notif) => (
                        <div
                            key={notif.id}
                            className={`group relative p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                !notif.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                            onClick={() => markAsRead(notif.id)}
                        >
                            {/* Icon + Content */}
                            <div className="flex items-start gap-3">
                                <div className="mt-1 text-xl transition-transform duration-200 group-hover:scale-110">
                                    {getIcon(notif.type)}
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900 dark:text-white">{notif.title}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{notif.message}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{notif.timestamp}</p>
                                </div>
                            </div>

                            {/* Einzel-Löschen (X bei Hover) */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeNotification(notif.id); // ← Löscht sofort
                                }}
                                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                                aria-label="Benachrichtigung löschen"
                            >
                                <BsX size={18}/>
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}